import { useCallback } from 'react';
import {
  RoomSchema,
  RoomStructureContent as RoomStructureContentSchema,
} from '../../../definitions/schemas';
import { RoomContentTypes } from '../../../enums/RoomStructureItemType';
import { trackDsrAssetLinkCopied } from '../../../lib/helper/dsrDeepLinkAnalytics';
import { useI18n } from '../../../lib/hook/useI18n';
import SnackbarStore from '../../../stores/observables/SnackbarStore';
import { appendSectionDeepLink } from './helper';
import { useSectionDeepLinkBaseUrl } from './useSectionDeepLinkBaseUrl';

export type UseBuyerAssetCopyLinkParams = {
  room: RoomSchema | undefined;
  structureContents: RoomStructureContentSchema[];
};

export const useBuyerAssetCopyLink = ({
  room,
  structureContents,
}: UseBuyerAssetCopyLinkParams) => {
  const { t } = useI18n();

  const roomDeepLinkBaseUrl = useSectionDeepLinkBaseUrl({
    canonicalRoomLink: room?.link,
    fallbackUrl: window.location.href,
    roomUuid: room?.uuid,
  });

  return useCallback(
    (sectionId: string) => {
      if (!roomDeepLinkBaseUrl || !navigator.clipboard || !room?.id) {
        return;
      }

      const row = structureContents.find((c) => c.id === sectionId);
      const assetType =
        row?.match?.type != null ? String(row.match.type) : 'unknown';

      navigator.clipboard
        .writeText(appendSectionDeepLink(roomDeepLinkBaseUrl, sectionId))
        .then(() => {
          SnackbarStore.show(t('rooms.editor.link_copied_to_clipboard'));
          if (row && RoomContentTypes.includes(row.type)) {
            trackDsrAssetLinkCopied({
              actor: 'buyer',
              assetId: sectionId,
              assetType,
              entryPoint: 'buyer_hover_icon',
              roomId: room.id,
            });
          }
        })
        .catch(() => {});
    },
    [room?.id, roomDeepLinkBaseUrl, structureContents, t]
  );
};
